import get from 'lodash.get';
import { graphql } from 'react-relay/legacy';
import { registrationFields } from '../helpers/checkoutForms';
import { trackExistingUserError } from '../helpers/checkoutTracking';
import { STATES, EMAIL_ERRORS } from '../constants/checkoutConstants';
import { isOfferPriceBookNameMismatch } from '../shipping/helpers/isOfferPriceBookNameMismatch';
import { addressSelectedAction } from './commonActions';
import {
    transactionBuyerChangedAction,
    shippingCompletedAction,
    registrationErrorAction,
    emptyShippingAddressAction,
} from './commonActions';

export function addAddress() {
    return {
        type: 'CHECKOUT_NEW_SHIPPING_ADDRESS',
    };
}

export function editAddress({ shippingAddress, paymentMethodToken, isEditingPartial }) {
    return addressSelectedAction({
        shippingState: STATES.STATE_EDIT,
        shippingAddress,
        paymentMethodToken,
        isEditingPartial,
    });
}

export function addressFormUpdated({ field, value, isAdding }) {
    if (registrationFields.includes(field)) {
        return { type: 'CHECKOUT_UPDATE_REGISTRATION_FORM', field, value };
    } else if (isAdding) {
        return { type: 'CHECKOUT_UPDATE_NEW_SHIPPING_FORM', field, value };
    } else {
        return { type: 'CHECKOUT_EDIT_SHIPPING_FORM', field, value };
    }
}

export function addressFormCanceled({
    isAdding,
    canUsePreFilledAddress,
    lastValidShippingAddressId,
    isSelectedAddressValid,
    shippingAddress,
    paymentMethodToken,
    dispatch,
}) {
    dispatch({ type: 'CHECKOUT_SET_SHIPPING_STATE', shippingState: STATES.STATE_SAVED });

    if (isAdding) {
        dispatch({ type: 'CHECKOUT_CANCEL_NEW_SHIPPING_FORM' });

        if (canUsePreFilledAddress) {
            dispatch(
                addressSelectedAction({
                    shippingState: STATES.STATE_SAVED,
                    shippingAddressId: lastValidShippingAddressId,
                })
            );
        }
    } else {
        dispatch({ type: 'CHECKOUT_CANCEL_SHIPPING_FORM_EDIT' });

        if (!isSelectedAddressValid) {
            dispatch(
                addressSelectedAction({
                    shippingState: STATES.STATE_EDIT,
                    shippingAddress,
                    paymentMethodToken,
                    isEditingPartial: true,
                })
            );
        }
    }
}

export function addressSaved(payload) {
    const {
        viewer,
        transaction,
        shippingAddress,
        paymentMethodToken,
        upcomingState,
        shippingAddressId,
        addressPriceBookName,
        offerPriceBookName,
        transactionType,
        dispatch,
    } = payload;
    const { isAuctionOrderType } = transaction || {};
    dispatch(
        addressSelectedAction({
            shippingState: upcomingState || STATES.STATE_SAVED,
            shippingAddress,
            paymentMethodToken,
        })
    );
    if (
        !isOfferPriceBookNameMismatch(
            offerPriceBookName,
            addressPriceBookName || null,
            Boolean(isAuctionOrderType),
            transactionType || null
        )
    ) {
        dispatch(
            shippingCompletedAction({
                viewer,
                transaction,
                newAddressId: get(shippingAddress, 'serviceId'),
                shippingAddressId,
            })
        );
    }
}

export function embeddedRegistrationSaved({ buyerId, address, isNewUser }) {
    return transactionBuyerChangedAction({
        buyerId,
        address,
        isNewUser,
    });
}

export function embeddedRegistrationSavedError(errMessage) {
    const ERRORS = [
        EMAIL_ERRORS.USER_EXISTS,
        EMAIL_ERRORS.EMAIL_ONLY_USER_EXISTS,
        EMAIL_ERRORS.SOCIAL_ACCOUNT_ALREADY_EXISTS,
    ];
    if (ERRORS.includes(errMessage)) {
        trackExistingUserError();
        return registrationErrorAction({
            errors: { email: errMessage },
        });
    }
    if (errMessage === EMAIL_ERRORS.CREDENTIALS_ARE_LEAKED) {
        return registrationErrorAction({
            errors: { password: errMessage },
        });
    }

    return registrationErrorAction({
        errors: {
            email:
                errMessage === EMAIL_ERRORS.EMAIL_INVALID
                    ? EMAIL_ERRORS.EMAIL_INVALID
                    : EMAIL_ERRORS.EMAIL_ERRORS_DEFAULT,
        },
    });
}

export function addressUpdated(payload) {
    const {
        shippingAddress,
        paymentMethodToken,
        upcomingAddressId,
        upcomingState,
        shippingAddressId: prevAddressId,
    } = payload;
    const isAddingAnotherAddress = upcomingAddressId === STATES.STATE_NEW;
    const isEditingAnotherAddress =
        !isAddingAnotherAddress &&
        !!upcomingAddressId &&
        upcomingAddressId !== prevAddressId &&
        upcomingState === STATES.STATE_EDIT;
    const addingAddressState = isAddingAnotherAddress ? STATES.STATE_NEW : STATES.STATE_SAVED;

    return addressSelectedAction({
        shippingState: isEditingAnotherAddress ? STATES.STATE_EDIT : addingAddressState,
        shippingAddress,
        paymentMethodToken,
    });
}

export function addressDeleted(payload) {
    const { shippingAddress, paymentMethodToken, isEditingPartial = false } = payload || {};
    if (shippingAddress) {
        return addressSelectedAction({
            shippingState: isEditingPartial ? STATES.STATE_EDIT : STATES.STATE_SAVED,
            shippingAddress,
            paymentMethodToken,
            isEditingPartial,
        });
    } else {
        return emptyShippingAddressAction();
    }
}

export function setShowTaxWarning(showTaxWarning) {
    return {
        type: 'CHECKOUT_SET_SHOW_TAX_WARINING',
        showTaxWarning,
    };
}

export function setShowAddressFormErrors(showAddressFormErrors) {
    return {
        type: 'CHECKOUT_SHOW_ADDRESS_FORM_ERRORS',
        showAddressFormErrors,
    };
}

export const shippingActionsViewerFragment = graphql`
    fragment checkoutShippingActions_viewer on Viewer {
        restrictionsEnabled: isEnabled(key: "restrictedMaterials")
    }
`;

export const shippingActionsTransactionFragment = graphql`
    fragment checkoutShippingActions_transaction on Transaction {
        serviceId
        isAuctionOrderType
        warnings(countryCode: $userCountryCode) {
            isCheckoutBlocked
        }
        buyerPriceTables(displayCurrency: $displayCurrency) {
            tables {
                rows {
                    value
                    rowType
                }
            }
        }
        isVatOrder
    }
`;

export default {
    viewer: shippingActionsViewerFragment,
    transaction: shippingActionsTransactionFragment,
};
